import { Controller } from "react-hook-form";
import { cn } from "@/lib/utils";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { formatNumber } from "@/utils/generic";
import { Slider } from "../shadcn/slider";
import { NumberInput } from "./NumberInputField";

interface INumberSlider {
  value: number;
  max?: number;
  min?: number;
  onChange: (v: number) => any;
  tooltip?: string;
  label: string;
  isDisabled?: boolean;
  step?: number;
  precision?: number;
  hideNumber?: boolean;
  hideInput?: boolean;
  name?: string;
  helperText?: string;
}

export const NumberSlider = ({
  value,
  min,
  max,
  tooltip,
  label,
  onChange,
  isDisabled,
  step,
  precision,
  hideNumber,
  hideInput,
  name,
  helperText,
}: INumberSlider) => {
  const handleChange = (valueNumber: number) => {
    onChange(valueNumber);
  };

  const handleSliderChange = (value: number) => {
    onChange(value);
  };

  return (
    <div className="w-full">
      {label && <NoxusFormLabel label={label} tooltip={tooltip} name={name} />}
      <div
        className={cn(
          "flex gap-6 items-center",
          // The hidden input is what gave this row an input's height.
          hideInput && "min-h-8"
        )}
      >
        {!hideInput && (
          <div className="w-24">
            <NumberInput
              value={value}
              min={min || 0}
              max={max}
              onChange={handleChange}
              isDisabled={isDisabled}
              placeholder="0"
              step={step}
              precision={precision}
            />
          </div>
        )}
        <div className="flex-1">
          <Slider
            value={[value]}
            onValueChange={(values) => handleSliderChange(values[0])}
            min={min || 0}
            max={max || 100}
            step={step}
            disabled={isDisabled}
            className="w-full"
            showValue={
              !hideNumber
                ? (v: number) => formatNumber(v) ?? String(v)
                : undefined
            }
          />
        </div>
      </div>
      {helperText && (
        <div className="mt-1.5 text-xs font-regular text-muted-foreground">
          {helperText}
        </div>
      )}
    </div>
  );
};

interface INumberSliderField {
  name: string;
  defaultValue?: number;
  max?: number;
  tooltip?: string;
  label: string;
  min?: number;
  control?: any;
  isDisabled?: boolean;
  step?: number;
  precision?: number;
  hideNumber?: boolean;
  hideInput?: boolean;
  helperText?: string;
}

export const NumberSliderField = ({
  name,
  label,
  defaultValue,
  max,
  min = 0,
  tooltip,
  control,
  isDisabled,
  step,
  precision,
  hideNumber,
  hideInput,
  helperText,
}: INumberSliderField) => {
  return (
    <Controller
      defaultValue={defaultValue}
      control={control}
      disabled={isDisabled}
      render={({ field }) => {
        return (
          <NumberSlider
            label={label}
            onChange={field.onChange}
            value={field.value}
            max={max}
            min={min}
            tooltip={tooltip}
            isDisabled={isDisabled}
            step={step}
            precision={precision}
            hideNumber={hideNumber}
            hideInput={hideInput}
            name={name}
            helperText={helperText}
          />
        );
      }}
      name={name}
    />
  );
};
